import React, { useEffect, useState, useRef } from 'react'
import { Box, Flex, useDisclosure, Text, Button } from '../../vanilla'
import { Hit, ProductModel } from '../../store/ProductStore'
import {observer} from 'mobx-react-lite'
import { useBasketStore, useOrderStore, useCustomerStore, useGlobalStore, useRetailMediaStore } from '../../store/hooks/useStore'
import { Link } from '../link'
import { HeartFilledIcon, HeartIcon } from '../icons'
import { useItemPrice } from '../algolia/util/useItemPrice'
import { ReviewStars } from '../review-stars'
import { PDPBadgeAsset } from '../cms/product-badges'
import {
  MultiBuyPromo,
  MultiBuyPromoOfferMessage,
  NearPromo,
} from '../product-detail/product-promo'
import useWishlistItem from '../../store/hooks/useWishlistItem'
import { useAlcoholRestrictions } from '../../hooks/use-alcohol-restrictions'
import { useLocation } from 'react-router-dom'
import LazyLoad from 'react-lazyload'
import { useAlgoliaAnalytics, useBookDeliveryPopover } from '../../contexts'
import useEinstein from '../../store/hooks/useEinstein'
import { useSelectItemTracking } from '../../analytics/select-item-tracking'
import { Price } from '../price'
import { mobilePLPBadges } from '../algolia/instantsearch/styles.css'
import UpdateCartItemQuantity from '../update-cart-quantity'
import { ATBButtonPopover } from '../add-to-basket-button-popover'
import { useBasketItem } from '../../store/hooks/useBasketItem'
import { LearnMoreAlcoholRestrictionModal } from '../alcohol-restriction-modal'
import { AlcoholRestrictionOverlay } from '../alcohol-restriction-overlay'
import { EVENT_NAMES_MAP } from '../../analytics/algolia'
import { getShowValues } from '../../pages/before-you-go/helpers'
import { CART_LOCATION } from '../../analytics/utils'
import { useAddToCartTracking } from '../../analytics/click-event-tracking'
import { useSlotExpiration } from '../../store/hooks/useSlotExpiration'
import { recommenderHit } from '../recommendations/styles.css'
import { formatProductName, isMobile, getDisabledButtonText, retainBrowsingPosition } from '../../utils/utils'
import { promotionModalContent } from '../product-detail/product-promo/styles.css'
import { UnavailableProductsOverlay } from '../unavailable-overlays'
import Separator from '../separator'
import { ProductImage } from '../product-detail/product-image'
import { TopRightProductBadgeGroup } from '../top-right-product-badges/top-right-product-badge-group'
import { ProductItem } from '../../store/BasketStore'
import { STAR_RATING_THRESHOLD } from '../../constants'
import { transformHit } from '../algolia/util/transformHit'
import { HitItemProps } from '../../types/algolia'
import { RestrictedProductsOverlay } from '../restricted-products-overlay'
import { SponsoredProductPopover } from '../sponsored-product-popover'
import { sendRMImpressions, sendRMClicks } from '../../analytics/retail-media-tracking'
import { useIntersectionObserver } from '../../hooks/use-intersection-observer'

const ProductTileContainer = ({
  hit,
  className,
  isPLPTile,
  idx,
  hitItemIndex,
  itemListName,
  itemListId,
  einsteinRecoParams,
  viewingCategoryId,
  showBadges,
  showPromotions,
  showRating,
  showNearPromo,
  isSponsored,
  sponsoredAdId
}: HitItemProps) => {
  const learnMoreAlcRestrictionModal = useDisclosure()
  const {events} = useAlgoliaAnalytics()
  const {sendViewProduct, sendClickRecommendations, sendClickCategoryProduct} = useEinstein()
  const basketStore = useBasketStore()
  const {pathname, search} = useLocation()
  const {
    setShowCheckoutSummary,
    customSitePreferences: {showMultibuyPriceBadge},
    selectedPriceDayOffset,
    selectedStoreId,
    selectedRegion
  } = useGlobalStore()

  // Restricted product classes
  const restrictedProductInfo = basketStore.getRestrictedProductInfo(hit.productClasses, hit.id)

  const {displayPrice, promotionPrice} = useItemPrice(hit as unknown as ProductModel)
  const {handleRemoveWishlistItem, isInWishlist, handleAddWishlistItem} = useWishlistItem(hit.id)
  const {checkIfProductAlcohol, isCurrentSlotAlcoholRestricted} = useAlcoholRestrictions()
  const isAlcohol = checkIfProductAlcohol(hit)
  const ref = useRef<HTMLDivElement | null>(null)
  // @todo format the algolia data to be consistent across PLP and PDP - happens in price component too
  // @ts-ignore
  const img = hit.image_groups?.[0]?.images?.[0] || hit.imageGroups?.[0]?.images?.[0]
  const {quantity} = useBasketItem(hit.id)
  const disabledProductBecauseOfRestriction = (isAlcohol && isCurrentSlotAlcoholRestricted) || (restrictedProductInfo.cannotAddToBasket && quantity === 0)
  const [clickedQuantity, setClickedQuantity] = useState(0)
  const {showBookDeliveryPopover} = useBookDeliveryPopover()
  const isBeforeYouGoPage = pathname === '/Iceland-BeforeYouGoPages'
  const {isReservationExpired, handleEditReservationExpired} = useSlotExpiration()
  const {editMode} = useOrderStore()
  const [searchQuery, setSearchQuery] = useState<string | null>(null)
  const hasBadgeAsset = !!hit.pdpBadgeAsset
  const {getApplicablePromotions} = useCustomerStore()
  const {multiBuyPromo, nearPromo} = getApplicablePromotions(hit.productPromotions)
  const {retailMediaUrl} = useRetailMediaStore()
  const [isRMImpressionsSent, setIsRMImpressionsSent] = useState(false)
  const {isOnScreen: isTrueView, observerKey} = useIntersectionObserver(ref, {
    useOnce: true,
    /* Offset for intersection track. Sometimes a user can view the top of the product list section on a PDP,
    but the actual banners are still not visible, so adding an offset to ensure that the event only fires when the user has scrolled to and viewed the product items,
    only then change observer state. */
    rootMargin: '-150px 0px -150px 0px',
  })
  useEffect(() => {
    setClickedQuantity(quantity)
  }, [basketStore.count])

  useEffect(() => {
    if (isTrueView && sponsoredAdId && !isRMImpressionsSent) {
      setIsRMImpressionsSent(true)
      sendRMImpressions(sponsoredAdId, retailMediaUrl)
  }
}, [hit, isTrueView])

  const addToBasket = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation()
    e.preventDefault()

    if (disabledProductBecauseOfRestriction) {
      return
    }

    if (editMode && isReservationExpired()) {
      // If in edit mode and slot has expired send to order history and don't add to basket
      handleEditReservationExpired()
    } else if (!basketStore.canAddItems) {
      showBookDeliveryPopover({
        clickedHitId: hit.id,
        bookDeliveryProps: {
          product: hit as ProductItem,
        },
      })
    } else {
      if (isBeforeYouGoPage) {
        setShowCheckoutSummary(true)
      }
      basketStore.addProductToUpdate({
        product: hit as ProductItem,
        quantity: 1,
        event: EVENT_NAMES_MAP.PLP_PRODUCT_ADD_TO_CART_AFTER_SEARCH,
        queryID: hit.__queryID,
      })
      setClickedQuantity(1)
    }
  }

  const productLink = `/p/${formatProductName(hit?.name)}/${hit.id}.html`

  const sendProductClickEvents = () => {
    /** Einstein Analytics events **/
    if (isPLPTile && viewingCategoryId)
      sendClickCategoryProduct({
        product: {id: hit.id},
        category: {
          id: viewingCategoryId,
        },
      })
    else if (einsteinRecoParams)
      sendClickRecommendations({
        product: {id: hit.id},
        __recoUUID: einsteinRecoParams.recoUUID,
        recommenderName: einsteinRecoParams.recommender,
      })
    else sendViewProduct({product: {id: hit.id}})

    /** Algolia Analytics events  **/
    events?.productListPage?.sendClickEvent({
      queryID: hit.__queryID,
      positions: [hit.__position],
      objectIDs: [hit.objectID],
    })
  }

  const toggleWishlist = async () => {
    if (isInWishlist) {
      handleRemoveWishlistItem()
      return
    }

    await handleAddWishlistItem(locationOfCart, viewingCategoryId, location.search, hitItemIndex)

    /** Algolia Analytics events  **/
    if (hit.__queryID) {
      events?.productListPage?.sendAddToFavouriteAfterSearchEvent({
        queryID: hit.__queryID,
        objectIDs: [hit.objectID],
      })
    } else {
      events?.productListPage?.sendAddToFavouriteEvent({
        objectIDs: [hit.objectID],
      })
    }
  }

  const {showBadge, showPromos, showRatings} = getShowValues(showBadges, showPromotions, showRating)

  const shouldShowRating = showRatings && (hit.productRating || 0) >= STAR_RATING_THRESHOLD
  const showMultiBuyPromoBadge = Boolean(showMultibuyPriceBadge) && !!multiBuyPromo && multiBuyPromo?.multibuyUnitPrice

  const sendSelectItemData = useSelectItemTracking()
  const sendAddToCartData = useAddToCartTracking(basketStore.basket)
  const isMobileFlag = isMobile()

  const getCartLocation = () => {
    if (typeof location != "undefined") {
      if (search) {
        return CART_LOCATION.SEARCH
      }
      if (location.pathname === '/Iceland-BeforeYouGoPages') {
        return CART_LOCATION.CHECKOUT
      }
      if (className === recommenderHit) {
        return CART_LOCATION.CAROUSEL
      }
      if (className === promotionModalContent) {
        return CART_LOCATION.CAROUSEL
      }
      return CART_LOCATION.PLP
    }
  }

  const locationOfCart = getCartLocation()
  const taggingInfo = { viewingCategoryId, itemListName, itemListId }

  useEffect(() => {
    if (search) {
      const params = new URLSearchParams(search)
      setSearchQuery(params.get('q'))
    }
  })

  return (
    <Box position="relative" className={className} ref={ref} key={observerKey}>
      <Button
        aria-label="Add to favourites"
        variant="unstyled"
        position="absolute"
        right="0px"
        top="0px"
        data-test-selector="favourite-icon"
        onClick={toggleWishlist}
      >
        {isInWishlist ? (
          <HeartFilledIcon color="accent0" boxSize="20px" />
        ) : (
          <HeartIcon color={{default: 'black', hover: 'accent0'}} boxSize="20px" />
        )}
      </Button>
      {/* Top Right Badges on mobile */}
        {isMobileFlag && (
          <TopRightProductBadgeGroup
            hit={hit}
            className={mobilePLPBadges}
            top={[15, 55, 95]}
            right={-5}
          />
        )}
      <Flex
        flexDirection={[isPLPTile ? 'row' : 'column', 'column']}
        flexWrap="wrap"
        align={['flex-start', 'center']}
        paddingX="8px"
        paddingY="12px"
        data-test-selector="product-list-item"
        bg="white"
        height="full"
      >
        <Flex flexDirection='column' alignItems='center' gap={['14px', '4px']}>
          <Box
            aria-label={hit.name}
            as={Link}
            href={productLink}
            ratio="1"
            width="full"
            style={{
              maxWidth: 160,
              flexBasis: 148,
              ...(disabledProductBecauseOfRestriction && {
                pointerEvents: 'none',
              }),
            }}
            marginTop={['0px', '32px']}
            flexShrink="0"
            position="relative"
            onClickCapture={() => {
              sendProductClickEvents(), sendSelectItemData(hit, hitItemIndex, taggingInfo), retainBrowsingPosition()
              if (sponsoredAdId) sendRMClicks(sponsoredAdId, retailMediaUrl);
            }}
          >
            {isAlcohol && disabledProductBecauseOfRestriction && (
              <AlcoholRestrictionOverlay onLearnMoreClick={learnMoreAlcRestrictionModal.onToggle} />
            )}
            {hit.isAddToCartDisabled && hit.disabledStatus && (
              <UnavailableProductsOverlay reasonForDisabledStatus={hit.disabledStatus} />
            )}
            {restrictedProductInfo?.cannotAddToBasket && hit.productClasses?.length > 0 && (
              <RestrictedProductsOverlay hit={hit}/>
            )}
            {/* Top Right Badges on desktop */}
            {!isMobileFlag && (
              <TopRightProductBadgeGroup 
                hit={hit}
                top={[15, 55, 95]}
                right={-5}
              />
            )}
            
            {typeof idx === 'number' && idx <= 5 ? (
              <ProductImage 
                image={img}
                imageAlt={img?.alt}
                isThumbnail={false}
                inDrawer={false}
                display="block"
                width="full"
                height="auto"
                marginTop={['12px', '0px']}
                paddingRight="8px"
                style={{maxHeight: 160, objectFit: 'contain'}}
              />
            ) : (
              <LazyLoad>
                <ProductImage 
                  image={img}
                  imageAlt={img?.alt}
                  isThumbnail={false}
                  inDrawer={false}
                  display="block"
                  width="full"
                  height="auto"
                  marginTop={['12px', '0px']}
                  paddingRight="8px"
                  style={{maxHeight: 160, objectFit: 'contain'}}
                />
              </LazyLoad>
            )}
          </Box>
          {/* Sponsored Product Message */}
            {isSponsored ?
              <Flex alignItems="center" gap="4px" paddingX='8px' paddingY='2px'>
                <Text variant='text2' color='gray800'>Sponsored</Text>
                <SponsoredProductPopover />
              </Flex>
            : null}
          </Flex>
        <Flex
          align={['flex-start', 'center']}
          flexBasis={['100px']}
          flexGrow="1"
          flexShrink={['0', '1', '1']}
          marginTop={['0px', '20px']}
          marginLeft={['12px', '0px']}
          flexDirection={'column'}
          height={['auto', 'full']}
          gap="12px"
          justifyContent="space-between"
        >
          {/* Near promo */}
          {showPromos && nearPromo && showNearPromo !== false ? <NearPromo promotion={nearPromo} hitId={hit.id}/> : null}
          <Text
            as={Link}
            aria-label={hit.name}
            href={productLink}
            variant="text4"
            textAlign={['left', 'center']}
            flex="1"
            height="full"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}
            overflow="hidden"
            color={{default: 'gray800', hover: 'gray700'}}
            marginTop={['32px', '0px']}
            marginBottom={['12px', '0px']}
            marginRight={['36px', '0px']}
            lineHeight="short"
            data-test-selector="product-list-item-name"
            onClickCapture={() => {
              sendProductClickEvents(), sendSelectItemData(hit, hitItemIndex, taggingInfo), retainBrowsingPosition()
              if (sponsoredAdId) sendRMClicks(sponsoredAdId, retailMediaUrl);
            }}
          >
            {hit.name}
          </Text>

          <Flex
            flexDirection="column"
            gap="12px"
            alignItems={['flex-start', 'center']}
            justifyContent="space-between"
          >
            {shouldShowRating && hit.productRating ? (
              <ReviewStars rating={hit.productRating} />
            ) : (
              <Box height="20px" display={['none', 'block']} />
            )}
            <Price
              price={displayPrice}
              promotionPrice={promotionPrice}
              priceInfo={hit.priceInfo}
              isPLPTile={isPLPTile}
            />
            {/* Show the separator and multiBuyBadge */}
            {showPromos && showMultiBuyPromoBadge ? (
              <>
                <Separator />
                <MultiBuyPromo promotion={multiBuyPromo} />
              </>
            ) : null}
          </Flex>
          {/* Badges: text or image */}
          {showBadge && hit.pdpBadgeAsset ? <PDPBadgeAsset badgeKey={hit.pdpBadgeAsset} /> : null}
        </Flex>
          {/* Nearness +x message */}
          {showPromos && nearPromo ? (
            <Box paddingTop="12px" marginLeft={["auto", "0px"]} marginTop={['auto', '0px']}>
              <MultiBuyPromoOfferMessage promotion={nearPromo} productId={hit.id} />
            </Box>
          ) : null}
        <Box
          width={[isPLPTile ? 'full' : 'auto', 'auto']}
          display={[isPLPTile ? 'flex' : 'block', 'block']}
          justifyContent="flex-end"
          marginTop={hasBadgeAsset ? ['8px', '16px'] : ['8px', '24px']}
          height='auto'
          minHeight={['auto', '56px']}
          paddingBottom={['8px', '4px']}
        >
          {hit.isAddToCartDisabled ? (
            <>
              {!disabledProductBecauseOfRestriction ? (
                  <Box>
                    <Button disabled variant="secondary" size='smWide' width={['auto', '150px']}>
                    {getDisabledButtonText(hit.disabledStatus)}
                    </Button>
                  </Box>
              ) : null}
            </>
          ) : (
            <UpdateCartItemQuantity
              product={hit as ProductItem}
              quantity={clickedQuantity || 0}
              setQuantity={setClickedQuantity}
              maxQuantity={restrictedProductInfo?.maxQuantityAllowed}
              cartLocation={locationOfCart}
              viewingCategoryId={viewingCategoryId}
              searchQuery={searchQuery}
              hitItemIndex={hitItemIndex}
              restrictionModal={learnMoreAlcRestrictionModal}
              cannotAddToBasket={restrictedProductInfo?.cannotAddToBasket}
              isSponsored={isSponsored}
              renderButton={
                !disabledProductBecauseOfRestriction ? (
                  <ATBButtonPopover
                    hit={transformHit(hit, selectedPriceDayOffset, selectedStoreId, selectedRegion) as Hit}
                    displayPrice={displayPrice}
                    isPLPTile={isPLPTile}
                    onButtonClick={(e) => {
                      addToBasket(e)
                      sendAddToCartData(
                        hit,
                        locationOfCart,
                        viewingCategoryId,
                        searchQuery,
                        hitItemIndex
                      )
                    }}
                    width={['auto', '150px']}
                    alignSelf="flex-end"
                  />
                ) : undefined
              }
            />
          )}
        </Box>
      </Flex>
      {isAlcohol && disabledProductBecauseOfRestriction ? (
        <LearnMoreAlcoholRestrictionModal {...learnMoreAlcRestrictionModal} />
      ) : null}
    </Box>
  )
}

export const ProductTile = observer(ProductTileContainer)